'use client';

import { useEffect } from 'react';
import { useStore } from '@/store/store';
import { trackExperimentExposure } from '@/tracking/amplitude/events/general/experiment-exposure';

export type UseExperimentExposureArgs = {
    flagKey?: string | null;
    variant?: string | null;
    experimentKey?: string;
};

/**
 * Fires a single Amplitude `$exposure` event when a component renders while
 * enrolled in an experiment. Source-agnostic: the caller supplies the
 * flag/variant from wherever they live (aggregation experiments map, Prismic
 * fields, etc.), so this is reusable across any component.
 *
 * No-ops when flagKey or variant is missing (i.e. the user is not enrolled),
 * or when no accountID is available (anonymous/not-yet-identified users).
 */
export const useExperimentExposure = ({ flagKey, variant, experimentKey }: UseExperimentExposureArgs): void => {
    const accountID = useStore((s) => s.userContext.accountSettings.accountID);

    useEffect(() => {
        if (!flagKey || !variant || !accountID) {
            return;
        }

        useStore.getState().tracking.sendEvent([
            trackExperimentExposure({
                flag_key: flagKey,
                variant,
                ...(experimentKey ? { experiment_key: experimentKey } : {})
            })
        ]);
    }, [flagKey, variant, experimentKey, accountID]);
};
